import { ShieldCheck } from "lucide-react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { useTranslations } from "@/shared/lang/useTranslations";

import type { ApiKeyEntity } from "../../services/apiKeysService";
import { PrincipalAvatar } from "./PrincipalAvatar";

export interface AccessApiKeyIdentityProps {
  apiKey: ApiKeyEntity;
  /** Relative "last used" label, already translated. */
  lastUsedLabel: string | null;
}

/**
 * Avatar + name + privilege badges + the mono meta line (key, IP allowance, last use).
 *
 * Shared by the table and the cards so a key reads the same on both. The meta line wraps rather than
 * truncating as a single strip: on a phone the three facts land on separate lines instead of the key being
 * cut mid-token, and a truncated API key identifier is worse than a wrapped one — it is no longer
 * recognisable as the key it names.
 */
export function AccessApiKeyIdentity({ apiKey, lastUsedLabel }: AccessApiKeyIdentityProps) {
  const { t } = useTranslations("settings");
  const ipCount = apiKey.allowed_ips?.length ?? 0;

  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <PrincipalAvatar name={apiKey.name} kind="key" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] font-medium text-foreground">{apiKey.name}</span>
          {apiKey.super_admin ? (
            <ColocaBadge variant="destructive" icon={ShieldCheck}>
              {t("access_apikeys_super_badge")}
            </ColocaBadge>
          ) : (
            apiKey.is_admin && (
              <ColocaBadge variant="warning" icon={ShieldCheck}>
                {t("access_apikeys_admin_badge")}
              </ColocaBadge>
            )
          )}
          {!apiKey.is_active && <ColocaBadge variant="secondary">{t("access_apikeys_inactive")}</ColocaBadge>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 font-mono text-[11px] text-muted-foreground">
          <span className="truncate">{apiKey.key}</span>
          <span aria-hidden>·</span>
          <span className="whitespace-nowrap">
            {ipCount === 0
              ? t("access_apikeys_any_ip")
              : ipCount === 1
                ? t("access_apikeys_ip_count_singular")
                : t("access_apikeys_ip_count_plural", { count: ipCount })}
          </span>
          {lastUsedLabel && (
            <>
              <span aria-hidden>·</span>
              <span className="whitespace-nowrap">{lastUsedLabel}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
